import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { startOf } from 'next/lib/date';
import { Select, Input, DateInput } from 'next/components/ui';
import { TimeframeWrapper } from './styled';

const OPERATOR_OPTIONS = [
  { label: 'previously', value: '' },
  { label: 'in the last', value: '< ago' },
  { label: 'since', value: 'since' },
];

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export const TimeframeOptions = ({
  times = 1,
  operator = '',
  value,
  onChange,
}) => {
  const timesRef = useRef(null);
  const [relativeTimeValue, setRelativeTimeValue] = useState('');

  useEffect(() => {
    if (timesRef.current) {
      timesRef.current.value = times;
    }
  }, [times]);

  useEffect(() => {
    if (operator === '< ago') {
      setRelativeTimeValue(value / MILLISECONDS_PER_DAY);
    }
  }, [operator, value]);

  const handleOperatorChange = ({ value: newOperator }) => {
    if (newOperator === '< ago') {
      onChange({ operator: newOperator, value: MILLISECONDS_PER_DAY });
    } else if (newOperator === 'since') {
      onChange({ operator: newOperator, value: Date.now() });
    } else {
      onChange({ operator: newOperator, value: null });
    }
  };

  const handleTimesBlur = ({ target: { valueAsNumber: newValue } }) => {
    let nextValue = newValue;

    if (!nextValue) {
      nextValue = 1;
      timesRef.current.value = nextValue;
    }

    onChange({
      times: nextValue,
    });
  };

  const handleRelativeTimeChange = ({ target: { valueAsNumber } }) =>
    void setRelativeTimeValue(valueAsNumber || '');

  const handleRelativeTimeBlur = ({ target: { valueAsNumber } }) => {
    let nextValue = valueAsNumber;

    if (!nextValue) {
      nextValue = 1;
      setRelativeTimeValue(nextValue);
    }

    onChange({
      value: nextValue * MILLISECONDS_PER_DAY,
    });
  };

  const handleDateChange = date =>
    void onChange({
      value: startOf(date, 'day').valueOf(),
    });

  return (
    <TimeframeWrapper>
      <span>At least</span>
      <Input
        type="number"
        name="times"
        aria-label="times"
        ref={timesRef}
        defaultValue={times}
        onBlur={handleTimesBlur}
      />{' '}
      <span>{times === 1 ? 'time' : 'times'}</span>{' '}
      <Select
        name="operator"
        value={OPERATOR_OPTIONS.find(
          ({ value: optionOperator }) => optionOperator === operator
        )}
        options={OPERATOR_OPTIONS}
        onChange={handleOperatorChange}
        portal
      />
      {operator === '< ago' && (
        <>
          <Input
            type="number"
            name="value"
            aria-label="value"
            value={relativeTimeValue}
            onChange={handleRelativeTimeChange}
            onBlur={handleRelativeTimeBlur}
          />
          <span>{relativeTimeValue === 1 ? 'day' : 'days'}</span>
        </>
      )}
      {operator === 'since' && (
        <DateInput
          future
          name="since"
          label="since"
          value={new Date(value)}
          onChange={handleDateChange}
        />
      )}
    </TimeframeWrapper>
  );
};

TimeframeOptions.propTypes = {
  // times & value are numeric, but may temporarily be empty string
  times: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  operator: PropTypes.oneOf(['', '< ago', 'since']),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
};
